/**
 * The name this page calls the platform.
 *
 * The platform's own brand is known to resellers, not to the end customers who run this test, so
 * the page carries whichever name it is deployed under: the second-to-last label of the hostname
 * it is served from, capitalised. Served from `test.example.dk`, the page calls itself Example.
 *
 * Note that the rule is positional, not public-suffix aware — a host under `example.co.uk` would
 * yield "Co". Set `VITE_BRAND` to override wherever the derivation is wrong.
 */

/** Used when the hostname yields nothing usable — an IP literal, or no hostname at all. */
export const FALLBACK_BRAND = 'Platform'

const IPV4 = /^\d{1,3}(\.\d{1,3}){3}$/

export function brandFromHostname(hostname: string, override?: string): string {
	const explicit = override?.trim()
	if (explicit) return explicit

	// Tolerates a Host header as well as a bare hostname.
	const host = hostname.trim().replace(/:\d+$/, '').replace(/\.$/, '')

	// An IP literal has no name in it: every label is a number, or it is bracketed IPv6.
	if (!host || IPV4.test(host) || host.includes(':') || host.startsWith('[')) return FALLBACK_BRAND

	const labels = host.split('.').filter(Boolean)
	const chosen = labels.length >= 2 ? labels[labels.length - 2] : labels[0]
	if (!chosen) return FALLBACK_BRAND

	return chosen.charAt(0).toUpperCase() + chosen.slice(1).toLowerCase()
}

export interface BrandLogo {
	src: string
	/**
	 * Rendered box in px, matching the file's own aspect ratio so the logo is not distorted and
	 * the header reserves its space before the image loads. Height is set per logo: these
	 * lockups are not the same shape.
	 */
	width: number
	height: number
}

export interface KnownBrand {
	/** Registrable domain. Matched against the host itself and any subdomain of it. */
	domain: string
	name: string
	logo: BrandLogo
}

/**
 * Domains whose audience already knows the brand, so the page shows the real logo rather than a
 * name derived from the hostname.
 *
 * The heights differ on purpose. TelecomX is a single line of type at 5:1, so it reads at wordmark
 * size; Powernet stacks a mark above its wordmark at 2:1, and at the same height its name would be
 * a fifth of the size and barely legible.
 */
const KNOWN_BRANDS: readonly KnownBrand[] = [
	// 1770x354, a single line of type.
	{ domain: 'telecomx.dk', name: 'TelecomX', logo: { src: '/telecomx.png', width: 120, height: 24 } },
	// 1000x504, a mark stacked above its wordmark.
	{ domain: 'powernet.dk', name: 'Powernet', logo: { src: '/powernet.png', width: 91, height: 46 } }
]

/**
 * The known brand this host belongs to, if any.
 *
 * Matches the apex and its subdomains, and nothing else: `nottelecomx.dk` and
 * `telecomx.dk.evil.com` both have the name in them and are both somebody else.
 */
export function knownBrandFor(hostname: string): KnownBrand | null {
	const host = hostname.trim().toLowerCase().replace(/:\d+$/, '').replace(/\.$/, '')
	if (!host) return null

	return KNOWN_BRANDS.find(
		({ domain }) => host === domain || host.endsWith(`.${domain}`)
	) ?? null
}

export interface ResolvedBrand {
	name: string
	logo: BrandLogo | null
}

/**
 * What the page calls itself, and what it shows at the top.
 *
 * An explicit override wins over everything, including a known brand — it is an instruction, not a
 * guess — and since it names the platform rather than picturing it, it comes with no logo.
 */
export function resolveBrand(hostname: string, override?: string): ResolvedBrand {
	if (override?.trim()) return { name: override.trim(), logo: null }

	const known = knownBrandFor(hostname)
	if (known) return { name: known.name, logo: known.logo }

	return { name: brandFromHostname(hostname), logo: null }
}

/** Resolved once. Guarded for a non-browser context so the copy module stays importable in tests. */
const resolved = resolveBrand(
	typeof location === 'undefined' ? '' : location.hostname,
	import.meta.env.VITE_BRAND
)

export const BRAND = resolved.name
export const BRAND_LOGO = resolved.logo
